// Copyright (c) 2021 WiseTime. All rights reserved.

import { flatten, uniq } from "ramda"
import { TimeGroup, TimeRow } from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { Site } from "../../generated/legebill/site/v4/site_pb"
import { RoundingPreference } from "../../generated/wisetime/type/site_preferences_pb"
import { SiteId } from "../user-context/slice"
import { IdsList } from "../tags/tags"
import { TagRef } from "../../generated/wisetime/user/type/tag_ref_pb"
import { ActivityTypeRef } from "../../generated/wisetime/user/type/activity_type_ref_pb"
import { TagCategoryRef } from "../../generated/wisetime/user/type/tag_category_ref_pb"

export type CollapsibleTimeGroup = {
  timeGroup: TimeGroup.AsObject,
  timeRows: TimeRow.AsObject[],
}

export const narrativeType = (currentSite: Site.AsObject | undefined): TimeGroup.NarrativeType =>
  currentSite?.postTimePrefs?.narrativeType ?? TimeGroup.NarrativeType.NARRATIVE_ONLY

export const secondsToHours = (seconds: number) => Number((seconds / secondsInHour).toFixed(2))

export const secondsInHour = 3_600
type DurationStep = 0.01 | 0.1 | 0.2 | 0.25
type FractionDigits = 1 | 2

export const secondsToHoursRounded = (seconds: number, roundingPreference: RoundingPreference): number => {
  const durationStep = computeDurationStep(roundingPreference)
  const fractionDigits = computeFractionDigits(roundingPreference)
  const number = Math.ceil((seconds / secondsInHour) / durationStep) * durationStep
  return Number(number.toFixed(fractionDigits))
}

export const totalHoursRounded = (timeGroups: TimeGroup.AsObject[], roundingPreference: RoundingPreference): number => {
  const totalDurationInSecs = timeGroups.reduce(
    (result, { totalDuration }) =>
      result + (secondsToHoursRounded(totalDuration?.seconds ?? 0, roundingPreference) * secondsInHour),
    0,
  )
  return secondsToHoursRounded(totalDurationInSecs, roundingPreference)
}

// Round fractional seconds
export const secondsRounded = (seconds: number): number => Math.round(seconds)
export const hoursToSecondsRounded = (hours: string): number => secondsRounded(Number(hours) * secondsInHour)

export const computeDurationStep = (roundingPreference: RoundingPreference): DurationStep => {
  if (roundingPreference === RoundingPreference.PERCENTAGE_POINTS) {
    // round to nearest 0.01 hrs
    return 0.01
  }

  if (roundingPreference === RoundingPreference.QUARTER_HOURS) {
    // round to nearest 0.25 hrs
    return 0.25
  }

  if (roundingPreference === RoundingPreference.TWELVE_MIN_UNITS) {
    // round to nearest 0.2 hrs
    return 0.2
  }

  if (roundingPreference === RoundingPreference.SIX_MIN_UNITS) {
    // round to nearest 0.1 hrs
    return 0.1
  }

  return 0.1
}

export const computeFractionDigits = (roundingPreference: RoundingPreference): FractionDigits => {
  if (roundingPreference === RoundingPreference.PERCENTAGE_POINTS
    || roundingPreference === RoundingPreference.QUARTER_HOURS) {
    // round to nearest 0.01 hrs or 0.25 hrs
    return 2
  }

  if (roundingPreference === RoundingPreference.SIX_MIN_UNITS
    || roundingPreference === RoundingPreference.TWELVE_MIN_UNITS) {
    // round to nearest 0.1 hrs or 0.2 hrs
    return 1
  }

  return 1
}

export const increment = (hours: number, roundingPreference: RoundingPreference): number => {
  const durationStep = computeDurationStep(roundingPreference)
  const fractionDigits = computeFractionDigits(roundingPreference)
  return Number((Number(hours) + durationStep).toFixed(fractionDigits))
}

export const decrement = (hours: number, roundingPreference: RoundingPreference, min = 0): number => {
  const durationStep = computeDurationStep(roundingPreference)
  const fractionDigits = computeFractionDigits(roundingPreference)
  const decrementedValue = Number((hours - durationStep).toFixed(fractionDigits))
  return decrementedValue < min ? min : decrementedValue
}

export type TimeGroupListItem = {
  id: string,
  label: string,
  narrative: string,
  tags: TagRef.AsObject[],
  tagCategories: TagCategoryRef.AsObject[],
  activityTypes: ActivityTypeRef.AsObject[],
}
export const matchTimeGroupIdList = (
  timeRows: TimeRow.AsObject[],
  timeGroupListItems: TimeGroupListItem[],
  currentSiteId: SiteId | undefined,
): IdsList => {
  const uniqFlatten = (nestedIdsLists: IdsList[]): IdsList => uniq(flatten(nestedIdsLists))
  const idListForCurrentSite = <T extends { id: string, siteId: string }>(items: T[]): IdsList =>
    items.reduce(
      (result: IdsList, { id, siteId }: T) => {
        if (siteId === currentSiteId) {
          result.push(id)
        }
        return result
      },
      [],
    )
  const tagIdsFromTimeRows = uniqFlatten(timeRows.map(t => idListForCurrentSite(t.tagsList)))
  const tagCategoryIdsFromTimeRows = uniqFlatten(timeRows.map(t => idListForCurrentSite(t.tagCategoriesList)))
  const activityTypeIdsFromTimeRows = uniqFlatten(timeRows.map(t => idListForCurrentSite(t.activityTypesList)))
  if (activityTypeIdsFromTimeRows.length > 1 || (
    tagIdsFromTimeRows.length === 0
    && activityTypeIdsFromTimeRows.length === 0
    && tagCategoryIdsFromTimeRows.length === 0
  )) {
    return []
  }
  return timeGroupListItems.reduce(
    (result: IdsList, { tags, tagCategories, activityTypes, id }: TimeGroupListItem) => {
      if ((activityTypeIdsFromTimeRows.length === 0 || activityTypeIdsFromTimeRows.every(id => activityTypes.some(a => a.id === id)))
        && (tagIdsFromTimeRows.length === 0 || tagIdsFromTimeRows.every(id => tags.some(t => t.id === id)))
        && (tagCategoryIdsFromTimeRows.length === 0 || tagCategoryIdsFromTimeRows.every(id => tagCategories.some(t => t.id === id)))
      ) {
        result.push(id)
      }
      return result
    },
    [],
  )
}
